/**
 * PlatformCache
 * 
 * Caches platform visual objects to prevent re-creation on every frame.
 * For moving platforms, only position updates are needed.
 */

import { Platform } from '../../../shared/CharacterTypes';

export interface PlatformVisual {
  platformId: string;
  phaserObject: any;
  isMoving: boolean;
}

export class PlatformCache {
  private platformVisuals = new Map<string, any>();
  private scene: unknown | null = null;

  constructor(scene: unknown | null = null) {
    this.scene = scene;
  }

  setScene(scene: unknown) {
    this.scene = scene;
  }

  /**
   * Cache a platform visual object
   */
  set(platformId: string, visual: any): void {
    this.platformVisuals.set(platformId, visual);
  }

  /**
   * Get a cached platform visual
   */
  get(platformId: string): any | undefined {
    return this.platformVisuals.get(platformId);
  }

  /**
   * Check if platform is cached
   */
  has(platformId: string): boolean {
    return this.platformVisuals.has(platformId);
  }

  /**
   * Clear all cached visuals
   */
  clear(): void {
    this.platformVisuals.clear();
  }

  /**
   * Get all cached platform IDs
   */
  getAllIds(): string[] {
    return Array.from(this.platformVisuals.keys());
  }

  /**
   * Update position of a moving platform visual
   */
  updatePlatformPosition(platform: Platform): boolean {
    const visual = this.platformVisuals.get(platform.id);
    if (visual && this.scene) {
      // Update Phaser object position
      if (visual.setPosition) {
        visual.setPosition(platform.x + (platform.width / 2), platform.y + (platform.height / 2));
        return true;
      }
    }
    return false;
  }

  /**
   * Get count of cached platforms
   */
  get size(): number {
    return this.platformVisuals.size;
  }
}